<template>
  <BaseModal :modalTitle="$t('W: Opret rapportskabelon')">
    <UiBaseFormVee
      submitOnCtrlEnter
      :errorText="errorText"
      :onSubmit="onSubmit">
      <UiInputTextFieldVee
        name="name"
        :label="$t('W: Internt navn')"
        :hint="$t('W: Navnet der bruges internt af laboratoriet, f.eks. i lister og opsætning.')"
        :rules="['required']"
        isRequired
        :unit="currentLocale.toUpperCase()"
        v-model="form.name" />

      <UiInputTextFieldVee
        name="displayTitle"
        :label="$t('W: Vist navn')"
        :hint="$t('W: Navnet kunden ser i stedet for det interne navn.')"
        :rules="['required']"
        isRequired
        :unit="currentLocale.toUpperCase()"
        v-model="form.displayTitle" />

      <!-- A distinct group, set apart from the two names above — the same mt-4 the template editor
           puts between its names and the partner logo. -->
      <div class="mt-4">
        <UiInputRadioGroupVee
          name="contentSetup"
          :label="$t('W: Rapportindhold')"
          :options="contentSetupOptions"
          v-model="form.contentSetup" />
      </div>

      <template #buttons>
        <UiBaseButton
          :text="$t('action.create')"
          :isLoading="isLoading"
          type="submit" />
      </template>
    </UiBaseFormVee>
  </BaseModal>
</template>

<script lang="ts">
import { useReportTemplateStore } from '@/stores/reportTemplateStore'
import { useAppStore } from '@/stores/appStore'
import { useToast } from '@/globals/typescript/utils/toast'
import { mapActions, mapState } from 'pinia'
import { useModalStore } from '@/stores/modalStore'
import { useUnsavedChangesGuard } from '@/composables/useUnsavedChangesGuard'
import { getContentSetupOptions } from '@/globals/typescript/utils/reportTemplateContentSetup'
import type { TReportTemplateContentSetup } from '@team-milva/lab-models/resources'

function createForm() {
  return {
    name: '',
    displayTitle: '',
    contentSetup: 'standard' as TReportTemplateContentSetup,
  }
}

export default {
  name: 'CreateReportTemplateModal',

  emits: ['close'],

  data() {
    return {
      unsavedGuard: useUnsavedChangesGuard(),
      form: createForm(),
      isLoading: false,
      errorText: '',
    }
  },

  computed: {
    ...mapState(useAppStore, ['currentLocale']),

    contentSetupOptions() {
      return getContentSetupOptions()
    },
  },

  methods: {
    ...mapActions(useReportTemplateStore, ['createReportTemplate']),

    async onSubmit(): Promise<void> {
      this.isLoading = true
      this.errorText = ''
      try {
        await this.createReportTemplate({
          title: this.form.name,
          displayTitle: this.form.displayTitle,
          // Lab is the only kind that can be created for now, so it is not asked for.
          type: 'lab',
          contentSetup: this.form.contentSetup,
        })
        this.unsavedGuard.takeSnapshot()
        useToast().success(this.$t('W: Rapportskabelon oprettet'))
        this.$emit('close')
      } catch {
        this.errorText = this.$t('error.common.standard')
        useToast().error(this.$t('W: Kunne ikke oprette rapportskabelon'))
      } finally {
        this.isLoading = false
      }
    },
  },

  created() {
    // The modal is always the top entry at the moment it is created, so this
    // is its own ModalEntry id — it lets ModalHost tell "the modal I am about
    // to close is dirty" apart from "something underneath it is dirty".
    this.unsavedGuard.register({
      snapshot: () => ({ ...this.form }),
      modalId: useModalStore().top?.id,
    })
  },

  beforeUnmount() {
    this.unsavedGuard.unregister()
  },
}
</script>
